import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from './Header';
import { apiService } from '../services/api';
import { Star, X, ChevronDown } from 'lucide-react';
import { type EmergingPhaseBucket, trialMatchesPhaseBuckets } from '../utils/emergingPhaseBuckets';
import OngoingTrialsGantt from './ongoing/OngoingTrialsGantt';
import TimelineAssumptionsPanel from './ongoing/TimelineAssumptionsPanel';
import {
  TreatmentLineFilter,
  TimelineAssumptions,
  loadTimelineAssumptions,
  inferAcceleratedApproval,
  compareTrialsByTimeline,
  OngoingTrialTimelineInput,
  trialPrimaryAssetId,
} from '../utils/ongoingTrialsTimeline';
import {
  buildMergedIndicationOptions,
  mergedIndicationIsFavorited,
  resolveMergedIndicationKey,
  trialMatchesTreatmentLineByIndication,
  trialMatchesTreatmentLineGlobally,
  parseIndicationLineName,
  type MergedIndicationOption,
} from '../utils/ongoingIndicationMerge';
import { expandOngoingTrialsResponse } from '../utils/expandOngoingTrialsResponse';

interface ComboOption {
  id: number;
  name: string;
}

interface ComboBoxProps {
  options: ComboOption[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  placeholder: string;
  emptyLabel?: string;
}

const ComboBox: React.FC<ComboBoxProps> = ({ options, selectedId, onSelect, placeholder, emptyLabel = 'No matches' }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const selectedName = useMemo(
    () => options.find((o) => o.id === selectedId)?.name ?? '',
    [options, selectedId]
  );

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 50);
    const tokens = q.split(/\s+/).filter(Boolean);
    return options
      .filter((o) => {
        const name = o.name.toLowerCase();
        return tokens.every((t) => name.includes(t));
      })
      .slice(0, 50);
  }, [options, query]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const commit = (id: number) => {
    onSelect(id);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) commit(opt.id);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const displayValue = open ? query : selectedName;

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        value={displayValue}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-9 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        spellCheck={false}
        autoComplete="off"
      />
      {selectedId != null && !open && (
        <button
          type="button"
          aria-label="Clear selection"
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(null);
            setQuery('');
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-slate-400 hover:text-slate-700"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      {open && (
        <div className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">{emptyLabel}</div>
          ) : (
            filtered.map((opt, idx) => (
              <button
                key={opt.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(opt.id);
                }}
                onMouseEnter={() => setHighlight(idx)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${
                  idx === highlight ? 'bg-indigo-50 text-indigo-800' : 'text-slate-700 hover:bg-slate-50'
                } ${selectedId === opt.id ? 'font-semibold' : ''}`}
              >
                <span className="truncate">{opt.name}</span>
                {selectedId === opt.id && <span className="text-[10px] uppercase tracking-wide text-indigo-600">selected</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

type BrowseMode = 'indication' | 'asset' | 'company';

interface OngoingTrialRow {
  id: number;
  nct_id?: string;
  trial_name?: string;
  full_title?: string;
  phases?: string;
  overall_status?: string;
  trial_status?: string;
  enrollment_size?: string;
  study_start_date?: string;
  organization_full_name?: string;
  last_update_submit_date?: string;
  last_updated_post_date?: string;
  primary_completion_date?: string;
  completion_date?: string;
  study_completion_date?: string;
  indication_details?: string;
  primary_endpoints?: string;
  secondary_endpoints?: string;
  sponsor?: string;
  indication_code?: string;
  indication_name?: string;
  asset_id?: number | null;
  asset_ids?: number[];
  asset_name?: string | null;
  company_id?: number | null;
  company_name?: string | null;
  /** All indication codes on this trial (from DB). */
  trial_indication_codes?: string[];
  data_sources?: string[];
  indication_details_text?: string;
  brief_summary?: string;
  intervention_names?: string[];
  line_of_therapy?: string | null;
  regimen_details?: unknown;
}

interface OngoingTrialGroup {
  indication_code: string;
  indication_name: string;
  trials: OngoingTrialRow[];
}

function phaseRank(phase?: string) {
  if (!phase) return 99;
  const normalized = phase.toLowerCase().replace(/\s+/g, '');
  if (normalized.includes('phase3')) return 1;
  if (normalized.includes('phase2/3') || normalized.includes('phase2-3')) return 2;
  if (normalized.includes('phase2')) return 3;
  if (normalized.includes('phase1/2') || normalized.includes('phase1-2')) return 4;
  if (normalized.includes('phase1')) return 5;
  return 99;
}

export interface OngoingTrialsViewProps {
  /** Render inside Landscapes page (no header / browse chrome). */
  embedded?: boolean;
  /** Lock indication browse to these emerging_disease_indications ids (stringified). */
  lockedIndicationCodes?: string[] | null;
  /** Fallback match when codes are missing: disease area label (e.g. SCLC). */
  lockedDiseaseArea?: string | null;
  lockedDisplayName?: string | null;
  /** When set, force treatment-line filter (used when disease-matched, not line-specific indication). */
  lockedTreatmentLine?: TreatmentLineFilter | null;
}

const OngoingTrialsView: React.FC<OngoingTrialsViewProps> = ({
  embedded = false,
  lockedIndicationCodes = null,
  lockedDiseaseArea = null,
  lockedDisplayName = null,
  lockedTreatmentLine = null,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const highlightIndicationCode = useMemo(() => {
    if (embedded) return '';
    const raw = searchParams.get('indication') || searchParams.get('highlight') || '';
    return raw.trim();
  }, [embedded, searchParams]);

  const [groups, setGroups] = useState<OngoingTrialGroup[]>([]);
  const [indicationOptions, setIndicationOptions] = useState<Array<{ code: string; name: string }>>([]);
  const [assetNameById, setAssetNameById] = useState<Map<number, string>>(new Map());
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [browseMode, setBrowseMode] = useState<BrowseMode>('indication');
  const [activeIndicationCode, setActiveIndicationCode] = useState<string | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [favoriteCodes, setFavoriteCodes] = useState<Set<string>>(new Set());
  const [phaseFilterBuckets, setPhaseFilterBuckets] = useState<EmergingPhaseBucket[]>([]);
  const [treatmentLineFilter, setTreatmentLineFilter] = useState<TreatmentLineFilter>('all');
  const [timelineAssumptions, setTimelineAssumptions] = useState<TimelineAssumptions>(() => loadTimelineAssumptions());
  const [acceleratedTrialIds, setAcceleratedTrialIds] = useState<Set<number>>(new Set());
  const [urlSynced, setUrlSynced] = useState(false);
  const [embeddedResolved, setEmbeddedResolved] = useState(false);

  const togglePhaseFilter = useCallback((b: EmergingPhaseBucket) => {
    setPhaseFilterBuckets((prev) => (prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]));
  }, []);

  const indicationNameByCode = useMemo(() => {
    const m = new Map<string, string>();
    indicationOptions.forEach((o) => m.set(o.code, o.name));
    groups.forEach((g) => m.set(g.indication_code, g.indication_name));
    return m;
  }, [indicationOptions, groups]);

  useEffect(() => {
    const loadTrials = async () => {
      try {
        setLoading(true);
        setError(null);
        const [trialsResponse, favoritesResponse] = await Promise.all([
          apiService.fetchOngoingTrials(),
          apiService.fetchOngoingTrialFavorites(),
        ]);
        if (trialsResponse?.success) {
          const nextGroups = expandOngoingTrialsResponse(trialsResponse);
          setGroups(nextGroups);
          const allTrials = nextGroups.flatMap((g: OngoingTrialGroup) => g.trials);
          setAcceleratedTrialIds(
            new Set(
              allTrials
                .filter((t: OngoingTrialRow) => inferAcceleratedApproval(t))
                .map((t: OngoingTrialRow) => t.id),
            ),
          );
          const apiAssets = trialsResponse.assets as Array<{ id: number; name: string }> | undefined;
          if (apiAssets && apiAssets.length > 0) {
            const m = new Map<number, string>();
            apiAssets.forEach((a) => {
              if (a && typeof a.id === 'number') m.set(a.id, a.name || `Asset #${a.id}`);
            });
            setAssetNameById(m);
          }
          const apiIndications = trialsResponse.indications as Array<{ code: string; name: string }> | undefined;
          if (apiIndications && apiIndications.length > 0) {
            setIndicationOptions(apiIndications);
          } else {
            const optionMap = new Map<string, string>();
            nextGroups.forEach((group: OngoingTrialGroup) => {
              if (group.indication_code && group.indication_name) {
                optionMap.set(group.indication_code, group.indication_name);
              }
            });
            const nextOptions = Array.from(optionMap.entries())
              .map(([code, name]) => ({ code, name }))
              .sort((a, b) => a.name.localeCompare(b.name));
            setIndicationOptions(nextOptions);
          }
        } else {
          setError(trialsResponse?.error || 'Failed to load ongoing trials');
        }
        if (favoritesResponse?.success) {
          const codes = new Set<string>((favoritesResponse.favorites || []).map((fav: { indication_code: string }) => fav.indication_code));
          setFavoriteCodes(codes);
        }
      } catch (err: unknown) {
        const msg = err && typeof err === 'object' && 'response' in err ? (err as { response?: { data?: { error?: string } } }).response?.data?.error : undefined;
        setError(msg || (err instanceof Error ? err.message : 'Failed to load ongoing trials'));
      } finally {
        setLoading(false);
      }
    };
    loadTrials();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await apiService.getCurrentUser();
        if (!cancelled) setIsSuperuser(Boolean(u?.is_superuser));
      } catch {
        if (!cancelled) setIsSuperuser(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Apply URL once when options exist. */
  useEffect(() => {
    if (embedded) {
      setBrowseMode('indication');
      setUrlSynced(true);
      return;
    }
    if (loading || urlSynced || indicationOptions.length === 0) return;
    const rawMode = (searchParams.get('browse') || '').toLowerCase();
    const mode: BrowseMode =
      rawMode === 'asset' || rawMode === 'company' ? (rawMode as BrowseMode) : 'indication';
    setBrowseMode(mode);

    const assetParam = searchParams.get('asset');
    if (assetParam && !Number.isNaN(Number(assetParam))) {
      setSelectedAssetId(Number(assetParam));
    }
    const companyParam = searchParams.get('company');
    if (companyParam && !Number.isNaN(Number(companyParam))) {
      setSelectedCompanyId(Number(companyParam));
    }

    const merged = buildMergedIndicationOptions(
      indicationOptions,
      groups.map((g) => g.indication_code),
    );

    if (mode === 'indication') {
      const fromUrl = highlightIndicationCode
        ? resolveMergedIndicationKey(highlightIndicationCode, merged)
        : null;
      setActiveIndicationCode(fromUrl ?? merged[0]?.key ?? null);
    }
    setUrlSynced(true);
  }, [embedded, loading, urlSynced, indicationOptions, groups, searchParams, highlightIndicationCode]);

  /** Deep links: switch indication when ?indication= changes after load. */
  useEffect(() => {
    if (embedded) return;
    if (loading || !urlSynced || indicationOptions.length === 0) return;
    if (!highlightIndicationCode) return;
    const merged = buildMergedIndicationOptions(
      indicationOptions,
      groups.map((g) => g.indication_code),
    );
    const resolved = resolveMergedIndicationKey(highlightIndicationCode, merged);
    if (!resolved) return;
    setBrowseMode('indication');
    setActiveIndicationCode(resolved);
  }, [embedded, highlightIndicationCode, loading, urlSynced, indicationOptions, groups]);

  const mergedIndicationOptions = useMemo(
    () => buildMergedIndicationOptions(indicationOptions, groups.map((g) => g.indication_code)),
    [indicationOptions, groups],
  );

  /** Embedded Landscapes page: lock indication (+ optional treatment line). */
  useEffect(() => {
    if (!embedded || loading || indicationOptions.length === 0) return;
    setBrowseMode('indication');
    setFavoritesOnly(false);

    const codes = (lockedIndicationCodes || []).map((c) => String(c).trim()).filter(Boolean);
    let resolved: string | null = null;

    if (codes.length === 1) {
      resolved = resolveMergedIndicationKey(codes[0], mergedIndicationOptions);
    } else if (codes.length > 1) {
      const hit = mergedIndicationOptions.find((o) =>
        codes.some((c) => o.indicationCodes.includes(c) || o.key === c),
      );
      resolved = hit?.key ?? null;
    }

    if (!resolved) {
      const needle = (lockedDisplayName || lockedDiseaseArea || '').trim().toLowerCase();
      if (needle) {
        const hit =
          mergedIndicationOptions.find((o) => o.displayName.trim().toLowerCase() === needle) ||
          mergedIndicationOptions.find((o) => {
            const d = o.displayName.trim().toLowerCase();
            return d.includes(needle) || needle.includes(d);
          }) ||
          mergedIndicationOptions.find((o) =>
            o.indicationCodes.some((c) => {
              const name = (indicationNameByCode.get(c) || '').toLowerCase();
              return name.includes(needle);
            }),
          );
        resolved = hit?.key ?? null;
      }
    }

    setActiveIndicationCode(resolved);
    setTreatmentLineFilter(lockedTreatmentLine || 'all');
    setEmbeddedResolved(true);
  }, [
    embedded,
    loading,
    indicationOptions.length,
    lockedIndicationCodes,
    lockedDiseaseArea,
    lockedDisplayName,
    lockedTreatmentLine,
    mergedIndicationOptions,
    indicationNameByCode,
  ]);

  const visibleMergedIndications = useMemo(() => {
    if (!favoritesOnly) return mergedIndicationOptions;
    return mergedIndicationOptions.filter((o) => mergedIndicationIsFavorited(o, favoriteCodes));
  }, [mergedIndicationOptions, favoritesOnly, favoriteCodes]);

  const activeMergedIndication = useMemo(
    () => mergedIndicationOptions.find((o) => o.key === activeIndicationCode) ?? null,
    [mergedIndicationOptions, activeIndicationCode],
  );

  useEffect(() => {
    if (!favoritesOnly || browseMode !== 'indication') return;
    if (activeMergedIndication && mergedIndicationIsFavorited(activeMergedIndication, favoriteCodes)) return;
    const firstFav = visibleMergedIndications[0]?.key;
    if (firstFav) setActiveIndicationCode(firstFav);
  }, [favoritesOnly, browseMode, favoriteCodes, visibleMergedIndications, activeMergedIndication]);

  useEffect(() => {
    if (embedded) return;
    if (browseMode !== 'indication') return;
    if (!activeIndicationCode) return;
    if (visibleMergedIndications.some((o) => o.key === activeIndicationCode)) return;
    setActiveIndicationCode(visibleMergedIndications[0]?.key ?? null);
  }, [embedded, browseMode, activeIndicationCode, visibleMergedIndications]);

  useEffect(() => {
    if (embedded) return;
    if (browseMode === 'indication' && activeMergedIndication && !activeMergedIndication.hasLineSplit) {
      setTreatmentLineFilter('all');
    }
  }, [embedded, browseMode, activeMergedIndication]);

  const assetOptions = useMemo(() => {
    const m = new Map<number, string>(assetNameById);
    for (const g of groups) {
      for (const t of g.trials) {
        const ids = t.asset_ids?.length ? t.asset_ids : t.asset_id != null ? [t.asset_id] : [];
        for (const aid of ids) {
          if (aid == null) continue;
          if (!m.has(aid)) {
            // Only fall back to per-trial name when this is the trial's first asset
            // (since trial.asset_name belongs to asset_ids[0]).
            const isFirst = ids[0] === aid;
            const label = isFirst && t.asset_name ? t.asset_name : `Asset #${aid}`;
            m.set(aid, label);
          }
        }
      }
    }
    return Array.from(m.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [groups, assetNameById]);

  const companyOptions = useMemo(() => {
    const m = new Map<number, string>();
    for (const g of groups) {
      for (const t of g.trials) {
        if (t.company_id != null) {
          const label = t.company_name?.trim() || `Company #${t.company_id}`;
          m.set(t.company_id, label);
        }
      }
    }
    return Array.from(m.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [groups]);

  useEffect(() => {
    if (browseMode !== 'asset' || assetOptions.length === 0) return;
    if (selectedAssetId != null && assetOptions.some((a) => a.id === selectedAssetId)) return;
    setSelectedAssetId(assetOptions[0]?.id ?? null);
  }, [browseMode, assetOptions, selectedAssetId]);

  useEffect(() => {
    if (browseMode !== 'company' || companyOptions.length === 0) return;
    if (selectedCompanyId != null && companyOptions.some((c) => c.id === selectedCompanyId)) return;
    setSelectedCompanyId(companyOptions[0]?.id ?? null);
  }, [browseMode, companyOptions, selectedCompanyId]);

  useEffect(() => {
    if (embedded || !urlSynced) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('browse', browseMode);
        if (browseMode === 'indication' && activeIndicationCode) {
          next.set('indication', activeIndicationCode);
        } else {
          next.delete('indication');
          next.delete('highlight');
        }
        if (browseMode === 'asset' && selectedAssetId != null) {
          next.set('asset', String(selectedAssetId));
        } else {
          next.delete('asset');
        }
        if (browseMode === 'company' && selectedCompanyId != null) {
          next.set('company', String(selectedCompanyId));
        } else {
          next.delete('company');
        }
        return next.toString() === prev.toString() ? prev : next;
      },
      { replace: true }
    );
  }, [
    embedded,
    browseMode,
    activeIndicationCode,
    selectedAssetId,
    selectedCompanyId,
    urlSynced,
    setSearchParams,
  ]);

  const filteredGroups = useMemo(() => {
    const hasSearch = searchQuery.trim().length > 0;
    const query = searchQuery.toLowerCase();

    const byPhase = (trials: OngoingTrialRow[]) => {
      if (phaseFilterBuckets.length === 0) return trials;
      return trials.filter((t) => trialMatchesPhaseBuckets(t.phases, phaseFilterBuckets));
    };

    const byTreatmentLineForIndication = (
      trial: OngoingTrialRow,
      groupIndicationCode: string,
      merged: MergedIndicationOption,
    ) => trialMatchesTreatmentLineByIndication(groupIndicationCode, treatmentLineFilter, merged);

    const byTreatmentLineGlobal = (trial: OngoingTrialRow) =>
      trialMatchesTreatmentLineGlobally(
        trial.trial_indication_codes?.length
          ? trial.trial_indication_codes
          : trial.indication_code
            ? [trial.indication_code]
            : [],
        treatmentLineFilter,
        indicationNameByCode,
      );

    const matchesSearch = (row: OngoingTrialRow) => {
      if (!hasSearch) return true;
      const indTokens = (row.trial_indication_codes || [])
        .map((c) => (c ? indicationNameByCode.get(String(c)) || String(c) : ''))
        .join(' ')
        .toLowerCase();
      const values = [
        row.nct_id,
        row.trial_name,
        row.full_title,
        row.phases,
        row.overall_status,
        row.trial_status,
        row.enrollment_size,
        row.study_start_date,
        row.organization_full_name,
        row.asset_name,
        row.company_name,
        row.indication_details,
        row.indication_name,
        row.primary_endpoints,
        row.secondary_endpoints,
        row.sponsor,
        indTokens,
        ...(row.data_sources || []),
      ];
      return values.some((value) => value && String(value).toLowerCase().includes(query));
    };

    const sortTrials = (trials: OngoingTrialRow[]) =>
      [...trials].sort((a, b) => phaseRank(a.phases) - phaseRank(b.phases));

    if (browseMode === 'indication') {
      if (!activeMergedIndication) return [];
      if (
        favoritesOnly &&
        !mergedIndicationIsFavorited(activeMergedIndication, favoriteCodes)
      ) {
        return [];
      }

      let trials: OngoingTrialRow[] = [];
      for (const group of groups) {
        if (!activeMergedIndication.indicationCodes.includes(group.indication_code)) continue;
        for (const trial of group.trials) {
          if (!matchesSearch(trial)) continue;
          if (
            !byTreatmentLineForIndication(trial, group.indication_code, activeMergedIndication)
          ) {
            continue;
          }
          trials.push({ ...trial, indication_code: group.indication_code });
        }
      }

      if (treatmentLineFilter === 'all') {
        const deduped = new Map<number, OngoingTrialRow>();
        for (const trial of trials) {
          if (!deduped.has(trial.id)) deduped.set(trial.id, trial);
        }
        trials = [...deduped.values()];
      }

      trials = byPhase(trials);
      if (trials.length === 0) return [];
      return [
        {
          indication_code: activeMergedIndication.key,
          indication_name: activeMergedIndication.displayName,
          trials: sortTrials(trials),
        },
      ];
    }

    if (browseMode === 'asset') {
      if (selectedAssetId == null) return [];
      const trialMap = new Map<number, OngoingTrialRow>();
      for (const g of groups) {
        for (const t of g.trials) {
          const ids = t.asset_ids?.length ? t.asset_ids : t.asset_id != null ? [t.asset_id] : [];
          if (!ids.includes(selectedAssetId)) continue;
          if (!matchesSearch(t)) continue;
          if (!byTreatmentLineGlobal(t)) continue;
          if (!trialMap.has(t.id)) trialMap.set(t.id, t);
        }
      }
      let trials = [...trialMap.values()];
      trials = byPhase(trials);
      const assetLabel = assetOptions.find((a) => a.id === selectedAssetId)?.name || `Asset #${selectedAssetId}`;
      if (trials.length === 0) return [];
      return [
        {
          indication_code: '__by_asset__',
          indication_name: assetLabel,
          trials: sortTrials(trials),
        },
      ];
    }

    if (browseMode === 'company') {
      if (selectedCompanyId == null) return [];
      return groups
        .map((group) => {
          let trials = group.trials
            .filter((t) => t.company_id === selectedCompanyId)
            .filter(matchesSearch)
            .filter(byTreatmentLineGlobal);
          trials = byPhase(trials);
          return { ...group, trials: sortTrials(trials) };
        })
        .filter((group) => group.trials.length > 0);
    }

    return [];
  }, [
    groups,
    searchQuery,
    browseMode,
    activeMergedIndication,
    selectedAssetId,
    selectedCompanyId,
    favoritesOnly,
    favoriteCodes,
    phaseFilterBuckets,
    treatmentLineFilter,
    assetOptions,
    indicationNameByCode,
  ]);

  const showTreatmentLineFilter = useMemo(() => {
    if (embedded && lockedTreatmentLine && lockedTreatmentLine !== 'all') return false;
    if (browseMode === 'indication') {
      return activeMergedIndication?.hasLineSplit ?? false;
    }
    return indicationOptions.some((o) => parseIndicationLineName(o.name).line != null);
  }, [embedded, lockedTreatmentLine, browseMode, activeMergedIndication, indicationOptions]);

  const otherIndicationsForAsset = useMemo(() => {
    if (browseMode !== 'asset' || selectedAssetId == null) return [];
    const codes = new Set<string>();
    for (const g of groups) {
      for (const t of g.trials) {
        const ids = t.asset_ids?.length ? t.asset_ids : t.asset_id != null ? [t.asset_id] : [];
        if (!ids.includes(selectedAssetId)) continue;
        (t.trial_indication_codes || [t.indication_code].filter(Boolean)).forEach((c) => {
          if (c && c !== 'unmapped') codes.add(String(c));
        });
      }
    }
    const merged = buildMergedIndicationOptions(indicationOptions, codes);
    return merged.map((o) => ({ code: o.key, name: o.displayName }));
  }, [browseMode, selectedAssetId, groups, indicationOptions]);

  const totalTrials = useMemo(
    () => filteredGroups.reduce((sum, group) => sum + group.trials.length, 0),
    [filteredGroups]
  );

  useEffect(() => {
    if (!highlightIndicationCode || loading) return;
    const id = window.setTimeout(() => {
      const groupEl = document.getElementById(`ongoing-trial-group-${highlightIndicationCode}`);
      if (groupEl) {
        groupEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
    return () => window.clearTimeout(id);
  }, [highlightIndicationCode, loading, filteredGroups]);

  const timelineTrials = useMemo((): OngoingTrialTimelineInput[] => {
    const trials = filteredGroups.flatMap((g) => g.trials);
    const enriched = trials.map((trial) => {
      const assetId = trialPrimaryAssetId(trial);
      const assetName =
        assetId != null
          ? assetNameById.get(assetId) || trial.asset_name || `Asset #${assetId}`
          : trial.asset_name || 'Unlinked asset';
      return { ...trial, asset_id: assetId, asset_name: assetName };
    });
    return enriched.sort((a, b) =>
      compareTrialsByTimeline(a, b, timelineAssumptions, (id) => acceleratedTrialIds.has(id)),
    );
  }, [filteredGroups, assetNameById, timelineAssumptions, acceleratedTrialIds]);

  const toggleAccelerated = useCallback((trialId: number) => {
    setAcceleratedTrialIds((prev) => {
      const next = new Set(prev);
      if (next.has(trialId)) next.delete(trialId);
      else next.add(trialId);
      return next;
    });
  }, []);

  const activeGroupForFavorite =
    browseMode === 'indication' && activeMergedIndication
      ? {
          indication_code: activeMergedIndication.key,
          indication_name: activeMergedIndication.displayName,
          indicationCodes: activeMergedIndication.indicationCodes,
        }
      : null;

  const shellClass = embedded
    ? 'text-slate-900 relative bg-transparent font-sans min-w-0'
    : 'min-h-screen text-slate-900 pb-20 relative bg-slate-50 font-sans';
  const mainClass = embedded
    ? 'w-full max-w-[100%] mx-auto space-y-4 min-w-0'
    : 'w-full max-w-[100%] mx-auto px-3 sm:px-4 lg:px-6 py-10 space-y-8 min-w-0';

  return (
    <div className={shellClass}>
      {!embedded && <Header />}

      <main className={mainClass}>
        {!embedded && (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8 space-y-3">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Ongoing Emerging Trials</h1>
              <p className="text-sm text-slate-500 mt-2">Timeline of emerging trials by indication, asset, and milestone.</p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              Disclaimer: This feature is in BETA mode. New updates to be released soon.
            </div>
          </div>
        )}

        {!loading && !error && !embedded && (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100">
            <div className="w-full max-w-[100%] mx-auto px-3 sm:px-4 lg:px-6 py-6 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Select view</h3>
                  <p className="text-sm text-slate-500">Choose how to browse trials.</p>
                </div>
                <div className="flex flex-wrap gap-2 p-1 rounded-xl bg-slate-100/90">
                  {(
                    [
                      { id: 'indication' as const, label: 'By indication' },
                      { id: 'asset' as const, label: 'By asset' },
                      { id: 'company' as const, label: 'By company' },
                    ] as const
                  ).map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setBrowseMode(tab.id)}
                      className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                        browseMode === tab.id ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {browseMode === 'indication' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="space-y-2 text-sm text-slate-600 sm:col-span-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Indication</span>
                      <label className="inline-flex items-center gap-2 text-xs text-slate-600 shrink-0 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={favoritesOnly}
                          onChange={(e) => setFavoritesOnly(e.target.checked)}
                          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Favorites only
                      </label>
                    </div>
                    <div className="relative">
                      <select
                        value={activeIndicationCode || ''}
                        onChange={(e) => setActiveIndicationCode(e.target.value || null)}
                        className="appearance-none w-full bg-white border border-slate-200 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
                        disabled={visibleMergedIndications.length === 0}
                      >
                        <option value="">
                          {visibleMergedIndications.length === 0
                            ? favoritesOnly
                              ? 'No favorite indications with trials'
                              : 'No indications with trials'
                            : 'Select indication'}
                        </option>
                        {visibleMergedIndications.map((option) => (
                          <option key={option.key} value={option.key}>
                            {option.displayName}
                            {mergedIndicationIsFavorited(option, favoriteCodes) ? ' ★' : ''}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                  </label>
                </div>
              )}

              {browseMode === 'asset' && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-400 block">Asset</label>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="w-full sm:flex-1">
                      <ComboBox
                        options={assetOptions}
                        selectedId={selectedAssetId}
                        onSelect={setSelectedAssetId}
                        placeholder="Type asset name…"
                        emptyLabel="No assets match"
                      />
                    </div>
                    {selectedAssetId != null && (
                      <div className="flex items-center gap-3 whitespace-nowrap">
                        <Link
                          to={`/emerging-assets/${selectedAssetId}`}
                          className="text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                        >
                          Open asset page →
                        </Link>
                        {isSuperuser && (
                          <Link
                            to={`/superuser/assets?focus=${selectedAssetId}`}
                            className="text-sm font-semibold text-amber-600 hover:text-amber-700"
                            title="Open admin tools to merge similar assets"
                          >
                            Merge similar →
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                  {otherIndicationsForAsset.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                      <span className="font-semibold text-slate-500 shrink-0">Also mapped to:</span>
                      {otherIndicationsForAsset.map(({ code, name }) => (
                        <button
                          key={code}
                          type="button"
                          onClick={() => {
                            setBrowseMode('indication');
                            setActiveIndicationCode(code);
                            setFavoritesOnly(false);
                          }}
                          className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-700"
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {browseMode === 'company' && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-400 block">Company</label>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="w-full sm:flex-1">
                      <ComboBox
                        options={companyOptions}
                        selectedId={selectedCompanyId}
                        onSelect={setSelectedCompanyId}
                        placeholder="Type company name…"
                        emptyLabel="No companies match"
                      />
                    </div>
                    {selectedCompanyId != null && (
                      <Link
                        to={`/emerging-companies/${selectedCompanyId}`}
                        className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 whitespace-nowrap"
                      >
                        All assets & reports →
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className={`bg-white shadow-card border border-gray-100 p-12 ${embedded ? 'rounded-xl' : 'rounded-[2rem]'}`}>
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          </div>
        ) : error ? (
          <div className={`bg-white shadow-card border border-gray-100 p-8 ${embedded ? 'rounded-xl' : 'rounded-[2rem]'}`}>
            <p className="text-center text-rose-600 font-medium">{error}</p>
          </div>
        ) : embedded && embeddedResolved && !activeIndicationCode ? (
          <div className="bg-white rounded-xl shadow-card border border-gray-100 p-8">
            <p className="text-center text-sm text-slate-500">
              No matching ongoing trials indication for this disease / line.
            </p>
          </div>
        ) : (
          <div
            id="ongoing-trials-table-panel"
            className={`bg-white shadow-card border border-gray-100 overflow-hidden min-w-0 ${
              embedded ? 'rounded-xl' : 'rounded-[2rem]'
            }`}
          >
            <div className="px-4 sm:px-6 py-4 border-b border-gray-100 bg-white flex flex-col gap-4 min-w-0">
              <div className="shrink-0">
                <h2 className={`${embedded ? 'text-lg' : 'text-xl'} font-bold text-slate-900`}>Timeline</h2>
                <p className="text-sm text-slate-500 mt-1">{totalTrials} trials</p>
              </div>
              <div className="w-full flex flex-col gap-4 min-w-0">
                {showTreatmentLineFilter && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 shrink-0">Treatment line</span>
                    <div className="flex flex-wrap gap-1.5">
                      {(
                        [
                          { id: 'all' as const, label: 'All' },
                          { id: 'untreated' as const, label: '1L' },
                          { id: 'previous_treated' as const, label: '2L+' },
                        ] as const
                      ).map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setTreatmentLineFilter(opt.id)}
                          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                            treatmentLineFilter === opt.id
                              ? 'bg-violet-600 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 shrink-0">Phase</span>
                  <div className="flex flex-wrap items-center gap-3">
                    {(['3', '2', '1'] as const).map((b) => (
                      <label
                        key={b}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          checked={phaseFilterBuckets.includes(b)}
                          onChange={() => togglePhaseFilter(b)}
                        />
                        Phase {b}
                      </label>
                    ))}
                  </div>
                  {phaseFilterBuckets.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setPhaseFilterBuckets([])}
                      className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 shrink-0"
                    >
                      Clear
                    </button>
                  )}
                </div>

                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search in current view…"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />

                <TimelineAssumptionsPanel assumptions={timelineAssumptions} onChange={setTimelineAssumptions} />
              </div>
            </div>

            {browseMode === 'indication' && !activeIndicationCode ? (
              <div className="px-6 py-10 text-center text-sm text-slate-500">Choose an indication above.</div>
            ) : browseMode === 'asset' && assetOptions.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-slate-500">No linked assets in the current trial set.</div>
            ) : browseMode === 'company' && companyOptions.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-slate-500">No company links on trials in this dataset.</div>
            ) : filteredGroups.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-slate-600 space-y-2">
                {phaseFilterBuckets.length > 0 ? (
                  <>
                    <p className="font-medium text-slate-800">No trials match the selected phase filters.</p>
                    <button
                      type="button"
                      onClick={() => setPhaseFilterBuckets([])}
                      className="text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                    >
                      Clear phase filters
                    </button>
                  </>
                ) : searchQuery.trim() ? (
                  <p>No trials match your search.</p>
                ) : (
                  <p>No trial rows for the current selection.</p>
                )}
              </div>
            ) : (
              <div className="p-4 sm:p-6 space-y-4">
                {browseMode === 'indication' && activeGroupForFavorite && (
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">{activeGroupForFavorite.indication_name}</h3>
                      <p className="text-xs text-slate-500">
                        {totalTrials} trial{totalTrials === 1 ? '' : 's'} · sorted by timeline
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!activeGroupForFavorite) return;
                        const isFavorite = mergedIndicationIsFavorited(
                          activeMergedIndication!,
                          favoriteCodes,
                        );
                        const nextAction = isFavorite ? 'remove' : 'add';
                        const results = await Promise.all(
                          activeGroupForFavorite.indicationCodes.map((code) =>
                            apiService.updateOngoingTrialFavorite({
                              indication_code: code,
                              indication_name:
                                indicationNameByCode.get(code) ||
                                activeGroupForFavorite.indication_name,
                              action: nextAction,
                            }),
                          ),
                        );
                        if (results.every((r) => r?.success)) {
                          setFavoriteCodes((prev) => {
                            const next = new Set(prev);
                            for (const code of activeGroupForFavorite.indicationCodes) {
                              if (isFavorite) next.delete(code);
                              else next.add(code);
                            }
                            return next;
                          });
                        }
                      }}
                      className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full border ${
                        activeMergedIndication &&
                        mergedIndicationIsFavorited(activeMergedIndication, favoriteCodes)
                          ? 'border-amber-300 text-amber-600 bg-amber-50'
                          : 'border-slate-200 text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Star
                        className={`w-3.5 h-3.5 ${
                          activeMergedIndication &&
                          mergedIndicationIsFavorited(activeMergedIndication, favoriteCodes)
                            ? 'fill-amber-400'
                            : ''
                        }`}
                      />
                      {activeMergedIndication &&
                      mergedIndicationIsFavorited(activeMergedIndication, favoriteCodes)
                        ? 'Favorited'
                        : 'Favorite'}
                    </button>
                  </div>
                )}

                <OngoingTrialsGantt
                  trials={timelineTrials}
                  assumptions={timelineAssumptions}
                  acceleratedTrialIds={acceleratedTrialIds}
                  onToggleAccelerated={toggleAccelerated}
                />
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default OngoingTrialsView;
